// Lightweight i18n dictionary for Mango Notes — EN / ES / IT / FR
export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
];

export const translations = {
  en: {
    versions: { title: "Version history", subtitle: "Pick a snapshot to roll back to. Mango Notes keeps the last saves so you can always come back.", restore: "Restore", restored: "Older version restored" },
    app: { tagline: "Tactile notes studio" },
    nav: { notes: "Notes", reminders: "Reminders", archive: "Archive", trash: "Trash", labels: "Labels", noLabels: "No labels yet" },
    top: { search: "Search title, content, checklist, labels...", searchActive: "{n} matches for \"{q}\"", noResults: "No notes match \"{q}\"", clear: "Clear search", buyCoffee: "Buy a coffee", openSidebar: "Open sidebar", toggleTheme: "Toggle theme", toggleView: "Toggle view" },
    sections: { pinned: "Pinned", others: "Others" },
    empty: {
      notes: "Your notes will appear here",
      reminders: "No upcoming reminders",
      archive: "Archived notes will appear here",
      trash: "Notes in trash will appear here",
    },
    quick: {
      placeholder: "Take a note...",
      title: "Title",
      listItem: "List item",
      close: "Close",
      newChecklist: "New checklist",
      togglePin: "Pin",
      colour: "Color",
      reminder: "Reminder",
      clearReminder: "Clear reminder",
    },
    editor: {
      title: "Title",
      placeholder: "Take a note...",
      toText: "To text",
      toChecklist: "To checklist",
      pin: "Pin",
      archive: "Archive",
      trash: "Trash",
      close: "Close",
    },
    labelsDialog: {
      title: "Edit labels",
      placeholder: "Create new label",
      empty: "No labels yet",
      created: "Label created",
      renamed: "Label renamed",
      deleted: "Label deleted",
    },
    actions: {
      pin: "Pin", unpin: "Unpin",
      archive: "Archive", unarchive: "Unarchive",
      trash: "Trash", restore: "Restore", deleteForever: "Delete forever",
      emptyTrash: "Empty trash", moreMenu: "More",
    },
    toasts: {
      noteCreated: "Note created", couldNotCreate: "Could not create note",
      updateFailed: "Update failed", movedToTrash: "Moved to trash",
      archived: "Archived", unarchived: "Unarchived", restored: "Restored",
      deleted: "Deleted", trashEmptied: "Trash emptied", loadFailed: "Failed to load",
    },
    tip: {
      title: "Buy Mango Notes a coffee",
      subtitle: "A tiny tip keeps the lamps on and the ideas flowing. Thank you ✨",
      yourName: "Your name (optional)",
      yourNamePh: "Anonymous",
      message: "Message (optional)",
      messagePh: "Say something nice...",
      send: "Send tip securely with Stripe",
      opening: "Opening Stripe...",
      recent: "Recent supporters",
      custom: "Custom",
      customAmountLabel: "Enter amount (£)",
      customHint: "Any amount between £1 and £500",
      invalidAmount: "Please enter an amount between £1 and £500",
      couldNotStart: "Could not start checkout",
    },
    install: {
      cta: "Install Mango Notes",
      installed: "Installed ✓",
      iosTitle: "Install Mango Notes on iOS",
      iosHelp: "In Safari, tap the Share button, then 'Add to Home Screen'.",
      close: "Got it",
    },
    conflict: {
      toast: "This note was edited on another device — your latest version was kept.",
      resolved: "Updated to latest version from your other device.",
    },
    collab: {
      updatedToast: "A collaborator updated this note",
      conflictToast: "A collaborator saved changes — you have unsaved edits",
      reloadBtn: "Load theirs",
    },
    share: {
      title: "Share this note",
      subtitle: "Create a private link — view only or editable — or send it to someone by email.",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      sendByEmail: "Send by email",
      emailPlaceholder: "friend@example.com",
      invalidEmail: "Please enter a valid email address",
      createLink: "Create link",
      sending: "Sending...",
      sent: "Email sent",
      failed: "Could not create share",
      revoke: "Revoke",
      revokedToast: "Share link revoked",
      activeLinks: "Active links",
      noLinks: "No active share links yet",
      viewCount: "{n} views",
      passphraseLabel: "Passphrase (optional)",
      passphrasePh: "Add a short password",
      passphraseHint: "Viewers will need this to read the note",
      locked: "🔒 Protected",
      roleLabel: "Access",
      roleViewer: "Can view",
      roleEditor: "Can edit",
      editorBadge: "✎ Editor",
    },
    unlock: {
      title: "This note is password protected",
      subtitle: "Enter the passphrase the sender shared with you.",
      placeholder: "Passphrase",
      unlock: "Unlock",
      wrong: "Incorrect passphrase",
    },
    editorToolbar: {
      bold: "Bold",
      italic: "Italic",
      bullet: "Bullet list",
      preview: "Preview",
      edit: "Edit",
    },
    notePw: {
      enterToUnlock: "Enter passphrase to unlock this note",
      setPassphrase: "Set a passphrase (min 3 characters) to lock this note",
      locked: "Note locked",
      unlocked: "Unlocked",
      wrong: "Incorrect passphrase",
      lockedCard: "Password-protected",
    },
    images: {
      attach: "Attach images",
      drop: "Drop images here or click to pick",
      dropActive: "Release to attach",
      uploading: "Uploading...",
      remove: "Remove image",
      removed: "Image removed",
      tooLarge: "Image too large — max 10MB",
      wrongType: "Please pick an image file",
      failed: "Upload failed",
    },
    export: {
      cta: "Export all",
      done: "Markdown download started",
      empty: "No notes to export",
    },
    publicView: {
      sharedBy: "Shared from",
      readOnly: "Read-only",
      collaborator: "Collaborator",
      saving: "Saving...",
      saved: "Saved",
      notFound: "This note is not available",
      notFoundHelp: "The link may have been revoked or the note was deleted.",
    },
    payment: {
      confirming: "Confirming your tip...",
      finalising: "Stripe is finalising the transaction.",
      thanks: "Thank you!",
      thanksAmount: "Your {amount} tip means the world.",
      wentWrong: "Something went wrong",
      couldNotConfirm: "We couldn't confirm your payment. Please try again.",
      stillProcessing: "Still processing",
      stillProcessingHelp: "Stripe is taking a bit longer than usual. You can safely head back; we'll record your tip once Stripe confirms.",
      backToNotes: "Back to notes",
      cancelTitle: "No worries — nothing was charged",
      cancelHelp: "You cancelled the checkout. Mango Notes is still here when you change your mind.",
    },
  },

  es: {
    versions: { title: "Historial de versiones", subtitle: "Elige una versión para volver atrás. Mango Notes guarda los últimos cambios.", restore: "Restaurar", restored: "Versión anterior restaurada" },
    app: { tagline: "Estudio de notas táctil" },
    nav: { notes: "Notas", reminders: "Recordatorios", archive: "Archivo", trash: "Papelera", labels: "Etiquetas", noLabels: "Aún no hay etiquetas" },
    top: { search: "Buscar título, contenido, lista, etiquetas...", searchActive: "{n} coincidencias para \"{q}\"", noResults: "Ninguna nota coincide con \"{q}\"", clear: "Limpiar búsqueda", buyCoffee: "Invítame un café", openSidebar: "Abrir menú", toggleTheme: "Cambiar tema", toggleView: "Cambiar vista" },
    sections: { pinned: "Fijadas", others: "Otras" },
    empty: {
      notes: "Tus notas aparecerán aquí",
      reminders: "Sin recordatorios próximos",
      archive: "Las notas archivadas aparecerán aquí",
      trash: "Las notas en papelera aparecerán aquí",
    },
    quick: {
      placeholder: "Escribe una nota...",
      title: "Título",
      listItem: "Elemento",
      close: "Cerrar",
      newChecklist: "Nueva lista",
      togglePin: "Fijar",
      colour: "Color",
      reminder: "Recordatorio",
      clearReminder: "Quitar recordatorio",
    },
    editor: {
      title: "Título",
      placeholder: "Escribe una nota...",
      toText: "A texto",
      toChecklist: "A lista",
      pin: "Fijar",
      archive: "Archivar",
      trash: "Papelera",
      close: "Cerrar",
    },
    labelsDialog: {
      title: "Editar etiquetas",
      placeholder: "Crear nueva etiqueta",
      empty: "Aún no hay etiquetas",
      created: "Etiqueta creada",
      renamed: "Etiqueta renombrada",
      deleted: "Etiqueta eliminada",
    },
    actions: {
      pin: "Fijar", unpin: "Desfijar",
      archive: "Archivar", unarchive: "Desarchivar",
      trash: "Papelera", restore: "Restaurar", deleteForever: "Eliminar para siempre",
      emptyTrash: "Vaciar papelera", moreMenu: "Más",
    },
    toasts: {
      noteCreated: "Nota creada", couldNotCreate: "No se pudo crear la nota",
      updateFailed: "Error al actualizar", movedToTrash: "Movida a la papelera",
      archived: "Archivada", unarchived: "Desarchivada", restored: "Restaurada",
      deleted: "Eliminada", trashEmptied: "Papelera vaciada", loadFailed: "Error al cargar",
    },
    tip: {
      title: "Invita un café a Mango Notes",
      subtitle: "Una pequeña propina mantiene las luces encendidas y las ideas fluyendo. ¡Gracias! ✨",
      yourName: "Tu nombre (opcional)",
      yourNamePh: "Anónimo",
      message: "Mensaje (opcional)",
      messagePh: "Di algo bonito...",
      send: "Enviar propina de forma segura con Stripe",
      opening: "Abriendo Stripe...",
      recent: "Patrocinadores recientes",
      custom: "Personalizado",
      customAmountLabel: "Introduce el importe (£)",
      customHint: "Cualquier importe entre £1 y £500",
      invalidAmount: "Introduce un importe entre £1 y £500",
      couldNotStart: "No se pudo iniciar el pago",
    },
    install: {
      cta: "Instalar Mango Notes",
      installed: "Instalada ✓",
      iosTitle: "Instalar Mango Notes en iOS",
      iosHelp: "En Safari, toca Compartir y luego 'Añadir a pantalla de inicio'.",
      close: "Entendido",
    },
    conflict: {
      toast: "Esta nota se editó en otro dispositivo — se mantuvo tu última versión.",
      resolved: "Actualizada a la última versión de tu otro dispositivo.",
    },
    share: {
      title: "Comparte esta nota",
      subtitle: "Crea un enlace privado — solo lectura o editable — o envíalo por correo.",
      copyLink: "Copiar enlace",
      linkCopied: "Enlace copiado",
      sendByEmail: "Enviar por correo",
      emailPlaceholder: "amigo@ejemplo.com",
      invalidEmail: "Introduce un correo válido",
      createLink: "Crear enlace",
      sending: "Enviando...",
      sent: "Correo enviado",
      failed: "No se pudo compartir",
      revoke: "Revocar",
      revokedToast: "Enlace revocado",
      activeLinks: "Enlaces activos",
      noLinks: "Aún no hay enlaces activos",
      viewCount: "{n} vistas",
      passphraseLabel: "Contraseña (opcional)",
      passphrasePh: "Añade una clave corta",
      passphraseHint: "Los lectores la necesitarán para abrir la nota",
      locked: "🔒 Protegido",
    },
    unlock: {
      title: "Esta nota está protegida con contraseña",
      subtitle: "Introduce la clave que el remitente te compartió.",
      placeholder: "Contraseña",
      unlock: "Desbloquear",
      wrong: "Contraseña incorrecta",
    },
    editorToolbar: {
      bold: "Negrita",
      italic: "Cursiva",
      bullet: "Lista con viñetas",
      preview: "Vista previa",
      edit: "Editar",
    },
    images: {
      attach: "Adjuntar imágenes",
      drop: "Suelta imágenes aquí o haz clic para elegir",
      dropActive: "Suelta para adjuntar",
      uploading: "Subiendo...",
      remove: "Quitar imagen",
      removed: "Imagen eliminada",
      tooLarge: "Imagen demasiado grande — máx. 10MB",
      wrongType: "Elige un archivo de imagen",
      failed: "Error al subir",
    },
    export: {
      cta: "Exportar todo",
      done: "Descarga de Markdown iniciada",
      empty: "No hay notas para exportar",
    },
    publicView: {
      sharedBy: "Compartido desde",
      readOnly: "Solo lectura",
      notFound: "Esta nota no está disponible",
      notFoundHelp: "El enlace puede haber sido revocado o la nota eliminada.",
    },
    payment: {
      confirming: "Confirmando tu propina...",
      finalising: "Stripe está finalizando la transacción.",
      thanks: "¡Gracias!",
      thanksAmount: "Tu propina de {amount} significa mucho.",
      wentWrong: "Algo salió mal",
      couldNotConfirm: "No pudimos confirmar tu pago. Inténtalo de nuevo.",
      stillProcessing: "Procesando",
      stillProcessingHelp: "Stripe está tardando un poco más de lo normal. Puedes volver con tranquilidad; registraremos tu propina cuando Stripe confirme.",
      backToNotes: "Volver a las notas",
      cancelTitle: "Sin problema — no se cobró nada",
      cancelHelp: "Cancelaste el pago. Mango Notes sigue aquí cuando cambies de idea.",
    },
  },

  it: {
    versions: { title: "Cronologia versioni", subtitle: "Scegli una versione a cui tornare. Mango Notes conserva gli ultimi salvataggi.", restore: "Ripristina", restored: "Versione precedente ripristinata" },
    app: { tagline: "Studio di note tattili" },
    nav: { notes: "Note", reminders: "Promemoria", archive: "Archivio", trash: "Cestino", labels: "Etichette", noLabels: "Nessuna etichetta" },
    top: { search: "Cerca titolo, contenuto, lista, etichette...", searchActive: "{n} corrispondenze per \"{q}\"", noResults: "Nessuna nota corrisponde a \"{q}\"", clear: "Pulisci ricerca", buyCoffee: "Offrimi un caffè", openSidebar: "Apri menu", toggleTheme: "Cambia tema", toggleView: "Cambia vista" },
    sections: { pinned: "Fissate", others: "Altre" },
    empty: {
      notes: "Le tue note appariranno qui",
      reminders: "Nessun promemoria imminente",
      archive: "Le note archiviate appariranno qui",
      trash: "Le note nel cestino appariranno qui",
    },
    quick: {
      placeholder: "Scrivi una nota...",
      title: "Titolo",
      listItem: "Elemento",
      close: "Chiudi",
      newChecklist: "Nuova lista",
      togglePin: "Fissa",
      colour: "Colore",
      reminder: "Promemoria",
      clearReminder: "Rimuovi promemoria",
    },
    editor: {
      title: "Titolo",
      placeholder: "Scrivi una nota...",
      toText: "A testo",
      toChecklist: "A lista",
      pin: "Fissa",
      archive: "Archivia",
      trash: "Cestino",
      close: "Chiudi",
    },
    labelsDialog: {
      title: "Modifica etichette",
      placeholder: "Crea nuova etichetta",
      empty: "Nessuna etichetta",
      created: "Etichetta creata",
      renamed: "Etichetta rinominata",
      deleted: "Etichetta eliminata",
    },
    actions: {
      pin: "Fissa", unpin: "Rimuovi dalla cima",
      archive: "Archivia", unarchive: "Rimuovi dall'archivio",
      trash: "Cestino", restore: "Ripristina", deleteForever: "Elimina definitivamente",
      emptyTrash: "Svuota cestino", moreMenu: "Altro",
    },
    toasts: {
      noteCreated: "Nota creata", couldNotCreate: "Impossibile creare la nota",
      updateFailed: "Aggiornamento fallito", movedToTrash: "Spostata nel cestino",
      archived: "Archiviata", unarchived: "Rimossa dall'archivio", restored: "Ripristinata",
      deleted: "Eliminata", trashEmptied: "Cestino svuotato", loadFailed: "Caricamento fallito",
    },
    tip: {
      title: "Offri un caffè a Mango Notes",
      subtitle: "Una piccola mancia tiene accese le luci e le idee in movimento. Grazie ✨",
      yourName: "Il tuo nome (opzionale)",
      yourNamePh: "Anonimo",
      message: "Messaggio (opzionale)",
      messagePh: "Scrivi qualcosa di carino...",
      send: "Invia mancia in sicurezza con Stripe",
      opening: "Apertura di Stripe...",
      recent: "Sostenitori recenti",
      custom: "Personalizzato",
      customAmountLabel: "Inserisci l'importo (£)",
      customHint: "Qualsiasi importo tra £1 e £500",
      invalidAmount: "Inserisci un importo tra £1 e £500",
      couldNotStart: "Impossibile avviare il pagamento",
    },
    install: {
      cta: "Installa Mango Notes",
      installed: "Installata ✓",
      iosTitle: "Installa Mango Notes su iOS",
      iosHelp: "In Safari, tocca Condividi e poi 'Aggiungi a Home'.",
      close: "Capito",
    },
    conflict: {
      toast: "Questa nota è stata modificata su un altro dispositivo — abbiamo mantenuto l'ultima versione.",
      resolved: "Aggiornata all'ultima versione dall'altro dispositivo.",
    },
    share: {
      title: "Condividi questa nota",
      subtitle: "Crea un link privato — sola lettura o modificabile — o invialo via email.",
      copyLink: "Copia link",
      linkCopied: "Link copiato",
      sendByEmail: "Invia via email",
      emailPlaceholder: "amico@esempio.com",
      invalidEmail: "Inserisci un indirizzo email valido",
      createLink: "Crea link",
      sending: "Invio...",
      sent: "Email inviata",
      failed: "Impossibile condividere",
      revoke: "Revoca",
      revokedToast: "Link revocato",
      activeLinks: "Link attivi",
      noLinks: "Nessun link attivo",
      viewCount: "{n} visualizzazioni",
      passphraseLabel: "Password (opzionale)",
      passphrasePh: "Aggiungi una password breve",
      passphraseHint: "I lettori ne avranno bisogno per aprire la nota",
      locked: "🔒 Protetta",
    },
    unlock: {
      title: "Questa nota è protetta da password",
      subtitle: "Inserisci la password condivisa dal mittente.",
      placeholder: "Password",
      unlock: "Sblocca",
      wrong: "Password errata",
    },
    editorToolbar: {
      bold: "Grassetto",
      italic: "Corsivo",
      bullet: "Elenco puntato",
      preview: "Anteprima",
      edit: "Modifica",
    },
    images: {
      attach: "Allega immagini",
      drop: "Trascina qui o clicca per scegliere",
      dropActive: "Rilascia per allegare",
      uploading: "Caricamento...",
      remove: "Rimuovi immagine",
      removed: "Immagine rimossa",
      tooLarge: "Immagine troppo grande — max 10MB",
      wrongType: "Seleziona un file immagine",
      failed: "Caricamento fallito",
    },
    export: {
      cta: "Esporta tutto",
      done: "Download Markdown avviato",
      empty: "Nessuna nota da esportare",
    },
    publicView: {
      sharedBy: "Condiviso da",
      readOnly: "Solo lettura",
      notFound: "Questa nota non è disponibile",
      notFoundHelp: "Il link potrebbe essere stato revocato o la nota eliminata.",
    },
    payment: {
      confirming: "Confermiamo la tua mancia...",
      finalising: "Stripe sta completando la transazione.",
      thanks: "Grazie!",
      thanksAmount: "La tua mancia di {amount} significa moltissimo.",
      wentWrong: "Qualcosa è andato storto",
      couldNotConfirm: "Non siamo riusciti a confermare il pagamento. Riprova.",
      stillProcessing: "In elaborazione",
      stillProcessingHelp: "Stripe sta impiegando un po' più del solito. Puoi tornare tranquillamente; registreremo la mancia quando Stripe la confermerà.",
      backToNotes: "Torna alle note",
      cancelTitle: "Nessun problema — nulla è stato addebitato",
      cancelHelp: "Hai annullato il pagamento. Mango Notes è ancora qui quando cambi idea.",
    },
  },

  fr: {
    versions: { title: "Historique des versions", subtitle: "Choisissez une version à restaurer. Mango Notes garde les dernières sauvegardes.", restore: "Restaurer", restored: "Ancienne version restaurée" },
    app: { tagline: "Studio de notes tactile" },
    nav: { notes: "Notes", reminders: "Rappels", archive: "Archive", trash: "Corbeille", labels: "Étiquettes", noLabels: "Aucune étiquette" },
    top: { search: "Rechercher titre, contenu, liste, étiquettes...", searchActive: "{n} résultats pour \"{q}\"", noResults: "Aucune note ne correspond à \"{q}\"", clear: "Effacer la recherche", buyCoffee: "Offrir un café", openSidebar: "Ouvrir le menu", toggleTheme: "Changer de thème", toggleView: "Changer la vue" },
    sections: { pinned: "Épinglées", others: "Autres" },
    empty: {
      notes: "Vos notes apparaîtront ici",
      reminders: "Aucun rappel à venir",
      archive: "Les notes archivées apparaîtront ici",
      trash: "Les notes de la corbeille apparaîtront ici",
    },
    quick: {
      placeholder: "Prendre une note...",
      title: "Titre",
      listItem: "Élément",
      close: "Fermer",
      newChecklist: "Nouvelle liste",
      togglePin: "Épingler",
      colour: "Couleur",
      reminder: "Rappel",
      clearReminder: "Supprimer le rappel",
    },
    editor: {
      title: "Titre",
      placeholder: "Prendre une note...",
      toText: "En texte",
      toChecklist: "En liste",
      pin: "Épingler",
      archive: "Archiver",
      trash: "Corbeille",
      close: "Fermer",
    },
    labelsDialog: {
      title: "Modifier les étiquettes",
      placeholder: "Créer une étiquette",
      empty: "Aucune étiquette",
      created: "Étiquette créée",
      renamed: "Étiquette renommée",
      deleted: "Étiquette supprimée",
    },
    actions: {
      pin: "Épingler", unpin: "Désépingler",
      archive: "Archiver", unarchive: "Désarchiver",
      trash: "Corbeille", restore: "Restaurer", deleteForever: "Supprimer définitivement",
      emptyTrash: "Vider la corbeille", moreMenu: "Plus",
    },
    toasts: {
      noteCreated: "Note créée", couldNotCreate: "Impossible de créer la note",
      updateFailed: "Échec de la mise à jour", movedToTrash: "Déplacée dans la corbeille",
      archived: "Archivée", unarchived: "Désarchivée", restored: "Restaurée",
      deleted: "Supprimée", trashEmptied: "Corbeille vidée", loadFailed: "Échec du chargement",
    },
    tip: {
      title: "Offrez un café à Mango Notes",
      subtitle: "Un petit pourboire garde les lumières allumées et les idées fluides. Merci ✨",
      yourName: "Votre nom (facultatif)",
      yourNamePh: "Anonyme",
      message: "Message (facultatif)",
      messagePh: "Dites quelque chose de gentil...",
      send: "Envoyer un pourboire en toute sécurité avec Stripe",
      opening: "Ouverture de Stripe...",
      recent: "Soutiens récents",
      custom: "Personnalisé",
      customAmountLabel: "Entrez le montant (£)",
      customHint: "N'importe quel montant entre 1 $ et 500 $",
      invalidAmount: "Entrez un montant entre 1 $ et 500 $",
      couldNotStart: "Impossible de démarrer le paiement",
    },
    install: {
      cta: "Installer Mango Notes",
      installed: "Installée ✓",
      iosTitle: "Installer Mango Notes sur iOS",
      iosHelp: "Dans Safari, touchez Partager puis 'Sur l'écran d'accueil'.",
      close: "Compris",
    },
    conflict: {
      toast: "Cette note a été modifiée sur un autre appareil — votre dernière version a été conservée.",
      resolved: "Mise à jour vers la dernière version de votre autre appareil.",
    },
    share: {
      title: "Partager cette note",
      subtitle: "Créez un lien privé — lecture seule ou modifiable — ou envoyez-le par e-mail.",
      copyLink: "Copier le lien",
      linkCopied: "Lien copié",
      sendByEmail: "Envoyer par e-mail",
      emailPlaceholder: "ami@exemple.com",
      invalidEmail: "Veuillez saisir une adresse e-mail valide",
      createLink: "Créer le lien",
      sending: "Envoi...",
      sent: "E-mail envoyé",
      failed: "Partage impossible",
      revoke: "Révoquer",
      revokedToast: "Lien révoqué",
      activeLinks: "Liens actifs",
      noLinks: "Aucun lien actif",
      viewCount: "{n} vues",
      passphraseLabel: "Mot de passe (facultatif)",
      passphrasePh: "Ajoutez un mot de passe court",
      passphraseHint: "Les lecteurs en auront besoin pour ouvrir la note",
      locked: "🔒 Protégée",
    },
    unlock: {
      title: "Cette note est protégée par mot de passe",
      subtitle: "Entrez le mot de passe que l'expéditeur a partagé.",
      placeholder: "Mot de passe",
      unlock: "Déverrouiller",
      wrong: "Mot de passe incorrect",
    },
    editorToolbar: {
      bold: "Gras",
      italic: "Italique",
      bullet: "Liste à puces",
      preview: "Aperçu",
      edit: "Modifier",
    },
    images: {
      attach: "Joindre des images",
      drop: "Déposez ici ou cliquez pour choisir",
      dropActive: "Relâchez pour joindre",
      uploading: "Téléversement...",
      remove: "Retirer l'image",
      removed: "Image retirée",
      tooLarge: "Image trop volumineuse — max 10 Mo",
      wrongType: "Choisissez un fichier image",
      failed: "Échec du téléversement",
    },
    export: {
      cta: "Tout exporter",
      done: "Téléchargement Markdown lancé",
      empty: "Aucune note à exporter",
    },
    publicView: {
      sharedBy: "Partagé depuis",
      readOnly: "Lecture seule",
      notFound: "Cette note n'est pas disponible",
      notFoundHelp: "Le lien a peut-être été révoqué ou la note supprimée.",
    },
    payment: {
      confirming: "Confirmation de votre pourboire...",
      finalising: "Stripe finalise la transaction.",
      thanks: "Merci !",
      thanksAmount: "Votre pourboire de {amount} compte énormément.",
      wentWrong: "Un problème est survenu",
      couldNotConfirm: "Nous n'avons pas pu confirmer votre paiement. Réessayez.",
      stillProcessing: "Toujours en traitement",
      stillProcessingHelp: "Stripe prend un peu plus de temps que d'habitude. Vous pouvez revenir en toute sécurité ; nous enregistrerons votre pourboire dès que Stripe confirmera.",
      backToNotes: "Retour aux notes",
      cancelTitle: "Aucun souci — rien n'a été débité",
      cancelHelp: "Vous avez annulé le paiement. Mango Notes est toujours là quand vous changez d'avis.",
    },
  },
};
